// RWA (HIP-3) venue helpers. The client only ever sees clean pairs ("GOLD/USDC:USDC");
// the dex namespace ("XYZ:GOLD/USDC:USDC" / "xyz:GOLD") is resolved here for HL calls only.
// The asset→venue index is DATA, not hardcoded: the backend builds it from live HL
// (highest-24h-volume dex per asset) and exposes it; the frontend fetches it.

export interface RwaVenueEntry {
  /** Cede exchange id backing this asset (e.g. "tradexyz"). */
  exchangeId: string;
  /** Lowercase HL dex id (e.g. "xyz"), uppercased in market symbols. */
  dexPrefix: string;
}

/** base (UPPER) → venue. Built dynamically backend-side, fetched frontend-side. */
export type RwaVenueIndex = Record<string, RwaVenueEntry>;

export interface RwaVenue {
  exchangeId: string;
  /** Dex-namespaced market symbol the SDK catalog uses (e.g. "XYZ:GOLD/USDC:USDC"). */
  prefixedPairSymbol: string;
  /** HL WS/info coin id (e.g. "xyz:GOLD") — candle/l2Book subscriptions. */
  hlCoin: string;
}

/** Venue for a clean pair against `index`, or null if not RWA (native HL). */
export function resolveRwaVenue(cleanPairSymbol: string, index: RwaVenueIndex): RwaVenue | null {
  const base = (cleanPairSymbol.split("/")[0] ?? "").toUpperCase();
  const venue = index[base];
  if (!venue) return null;
  return {
    exchangeId: venue.exchangeId,
    // SDK quotes every HIP-3 perp in USDC even when settlement differs (hyna/cash).
    prefixedPairSymbol: `${venue.dexPrefix.toUpperCase()}:${base}/USDC:USDC`,
    hlCoin: `${venue.dexPrefix}:${base}`,
  };
}

/** True iff the clean pair is an RWA market in `index`. */
export function isRwaPairSymbol(cleanPairSymbol: string, index: RwaVenueIndex): boolean {
  return resolveRwaVenue(cleanPairSymbol, index) !== null;
}

/** All RWA bases in `index`, sorted. */
export function listRwaBases(index: RwaVenueIndex): string[] {
  return Object.keys(index).sort();
}

/** Strip a `DEX:` namespace → clean pair ("XYZ:GOLD/…" → "GOLD/…"); clean passes through. */
export function toCleanPairSymbol(pairSymbol: string): string {
  const [head = "", ...rest] = pairSymbol.split("/");
  const separatorIndex = head.indexOf(":");
  const cleanBase = separatorIndex === -1 ? head : head.slice(separatorIndex + 1);
  return [cleanBase, ...rest].join("/");
}
